import { useEffect, useMemo, useState } from 'react';

// Client-side pagination for an already-fetched list. Slices `items` into pages of `pageSize`,
// tracks the current page, and clamps it back when the list shrinks (a deletion or a filter
// narrowing the set) so the user is never stranded on an empty page past the end. For a "reset to
// page 1 when the underlying set changes entirely" (e.g. switching sport), remount the consumer
// via a `key` rather than watching the array here — the array identity churns on every refetch.
export function usePagination<T>(items: T[], pageSize: number) {
  const [page, setPage] = useState(1);
  const pageCount = Math.max(1, Math.ceil(items.length / pageSize));

  // Clamp when the derived count drops below the current page (e.g. deleting the last row on the
  // final page pulls us back to the new last page instead of showing an empty table).
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const pageItems = useMemo(
    () => items.slice((page - 1) * pageSize, page * pageSize),
    [items, page, pageSize],
  );

  return { page, setPage, pageCount, pageItems };
}
